import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { collection, CollectionReference, FirestoreError, onSnapshot, orderBy, query, QueryConstraint, where } from "firebase/firestore";
import { Button, Group, SegmentedControl, Select, Switch, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useFirestore } from "../../helpers/firebaseContext";
import { CtcPurchaseOrder } from "../../types";
import { CtcPoStatusCode, CTC_PO_STATUS_TAGS } from "../../../../shared/ctcLifecycle";
import { useCtcUser } from "./ctc_helpers";
import { LivePage, useLivePagedQuery } from "./useLivePagedQuery";
import { CtcPager, CtcQueryError } from "./ctc_pager";
import { CtcSignatureButton } from "./ctc_profile";
import { CtcPanel, ctcClearable } from "./ctc_panel";
import { ListViewButton, OrdersTable, listMemory, sortPath, sortRows, useCtcListBackend, useDebounced, useListView, useMemoryState, CTC_OPEN_LANES } from "./ctc_orders_table";
import { CtcOrdersTypesense, CtcWatchListTypesense, StatusSelect } from "./ctc_orders_search";

/*
 * Firestore versions of the Watch list and Purchase orders (Typesense versions: ctc_orders_search.tsx;
 * CtcOrders / CtcWatchList at the end pick one with the Firestore / Typesense switch).
 * Every filter here is a server-side Firestore query with a composite index in
 * firestore.indexes.json (ctc_purchase_orders). Lists are live (onSnapshot) and cursor-paginated.
 * KFH users always carry where(customerId == their claim) — the security rules require it.
 * View (one button → modal): shown columns + one sort (Reference / Beneficiary / Amount / Created /
 * Updated, asc or desc), remembered per list in this browser.
 *  - Purchase orders: the sort is part of the query, so it covers all orders. A sort and a status
 *    filter don't combine (picking one resets the other); desk users need no index, bank users one per
 *    field and direction (customerId + field).
 *  - Watch list: every open order is loaded live (no limit), sorted and paged in the browser — no index.
 */

const PAGE_SIZE = 25;

/** Every document of a live query (no limit), for lists sorted and paged in the browser. */
const useLiveQueryAll = <T,>(ref: CollectionReference, constraints: QueryConstraint[], key: string) => {
    const [state, setState] = useState<{ rows: T[]; loading: boolean; error?: FirestoreError }>({ rows: [], loading: true });
    useEffect(() => {
        setState((s) => ({ ...s, loading: true }));
        return onSnapshot(query(ref, ...constraints),
            (snap) => setState({ rows: snap.docs.map((d) => ({ ...(d.data() as T), id: d.id })), loading: false }),
            (error) => setState({ rows: [], loading: false, error }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, key]);
    return state;
};

/** Pages already-loaded rows for CtcPager (same bar as the server-paged lists); the page is remembered per list. */
const useClientPage = <T extends { id?: string },>(rows: T[], loading: boolean, error: FirestoreError | undefined, resetKey: string): LivePage<T> => {
    const [page, setPageState] = useState(() => (listMemory.get(`page:${resetKey}`) as number | undefined) ?? 0);
    const setPage = (p: number) => { listMemory.set(`page:${resetKey}`, p); setPageState(p); };
    const shownKey = useRef(resetKey);
    useEffect(() => {
        if (shownKey.current === resetKey) return;
        shownKey.current = resetKey;
        setPageState((listMemory.get(`page:${resetKey}`) as number | undefined) ?? 0);
    }, [resetKey]);
    const last = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
    const current = Math.min(page, last);
    return {
        rows: rows.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE), loading, error, page: current, pageSize: PAGE_SIZE, total: rows.length,
        hasNext: current < last, hasPrev: current > 0, next: () => setPage(current + 1), prev: () => setPage(current - 1), first: () => setPage(0),
    };
};

type SearchMode = "reference" | "beneficiary" | "clientRef";

/** All purchase orders: status filter or one search (reference prefix, beneficiary no., client ref). */
const CtcOrdersFirestore = ({ backendSwitch }: { backendSwitch: ReactNode }) => {
    const db = useFirestore();
    const user = useCtcUser();
    // Filters survive opening an order and coming back (with the page, see useLivePagedQuery)
    const [status, setStatus] = useMemoryState<string | null>("orders:status", null);
    const [mode, setMode] = useMemoryState<SearchMode>("orders:mode", "reference");
    const [searchInput, setSearchInput] = useMemoryState("orders:search", "");
    const search = useDebounced(searchInput.trim());
    const view = useListView("orders", { field: "created", dir: "desc" });
    const { field: sortField, dir: sortDir } = view.sort;

    const ref = useMemo(() => collection(db, "ctc_purchase_orders"), [db]);
    const { constraints, key } = useMemo(() => {
        const base: QueryConstraint[] = user.customerId ? [where("customerId", "==", user.customerId)] : [];
        if (search) {
            if (mode === "reference") {
                const q = search.toUpperCase();
                return { key: `ref:${q}:${user.customerId ?? ""}`, constraints: [...base, where("referenceNumber", ">=", q), where("referenceNumber", "<=", `${q}`), orderBy("referenceNumber")] };
            }
            const field = mode === "beneficiary" ? "beneficiary.number" : "clientRefNo";
            return { key: `${mode}:${search}:${user.customerId ?? ""}`, constraints: [...base, where(field, "==", search), orderBy("created", "desc")] };
        }
        if (status) {
            return { key: `status:${status}:${user.customerId ?? ""}`, constraints: [...base, where("tags.poStatus.code", "==", status), orderBy("created", "desc")] };
        }
        // No status filter: the chosen sort, over all orders
        return {
            key: `sort:${sortField}:${sortDir}:${user.customerId ?? ""}`,
            constraints: [...base, orderBy(sortPath(sortField), sortDir)],
        };
    }, [search, mode, status, user.customerId, sortField, sortDir]);
    const page = useLivePagedQuery<CtcPurchaseOrder>(ref, constraints, key, PAGE_SIZE);

    return (
        <CtcPanel>
            <Group justify="space-between" mb="md" align="flex-end">
                <Group align="flex-end">
                    <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as SearchMode)} data={[
                        { value: "reference", label: "Reference" },
                        { value: "beneficiary", label: "Beneficiary number" },
                        { value: "clientRef", label: "Client reference" },
                    ]} />
                    <TextInput
                        leftSection={<IconSearch size={14} />}
                        placeholder={mode === "reference" ? "KFH-26-00…" : mode === "beneficiary" ? "exact beneficiary number" : "exact client reference"}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.currentTarget.value)}
                        w={240}
                        {...ctcClearable(searchInput, () => setSearchInput(""))}
                    />
                    <Select
                        placeholder="Any status"
                        clearable
                        disabled={!!search}
                        value={status}
                        onChange={(v) => {
                            setStatus(v);
                            // A status filter lists newest first: it doesn't combine with another sort
                            if (v && !view.isDefaultSort) view.apply({ columns: view.columns, sort: view.defaultSort });
                        }}
                        data={Object.values(CtcPoStatusCode).map((code) => ({ value: code, label: CTC_PO_STATUS_TAGS[code].title }))}
                        w={280}
                    />
                </Group>
                <Group align="flex-end">
                    {backendSwitch}
                    <ListViewButton view={view} note="Sorts all orders. Choosing a sort clears the status filter; search results keep their own order."
                        onApply={(sortChanged) => sortChanged && setStatus(null)} />
                </Group>
            </Group>
            {search && <Text size="xs" c="dimmed" mb="xs">Searching ignores the status filter and the sort.</Text>}
            <CtcQueryError error={page.error} />
            <OrdersTable orders={page.rows} loading={page.loading} columns={view.columns} onColumnsChange={(columns) => view.apply({ columns, sort: view.sort })} emptyText={search ? "No match." : "No purchase orders yet."} />
            <CtcPager page={page} />
        </CtcPanel>
    );
};

/** Watch list: every open order (closed ones drop out), oldest first, so the team can clear it by the end of the day. */
const CtcWatchListFirestore = ({ backendSwitch }: { backendSwitch: ReactNode }) => {
    const db = useFirestore();
    const user = useCtcUser();
    const [onlyMine, setOnlyMine] = useMemoryState("watch:onlyMine", false);
    const [status, setStatus] = useMemoryState<string | null>("watch:status", null);
    const view = useListView("watch", { field: "created", dir: "asc" }); // oldest waiting first

    const ref = useMemo(() => collection(db, "ctc_purchase_orders"), [db]);
    const { constraints, key } = useMemo(() => {
        const laneFilter = where("lane", "in", CTC_OPEN_LANES);
        const scope: QueryConstraint[] = user.customerId ? [where("customerId", "==", user.customerId)] :
            onlyMine ? [where("assignedDealerEmail", "==", user.email)] : [];
        return {
            key: `watch:${user.customerId ?? ""}:${onlyMine ? user.email : ""}`,
            constraints: [...scope, laneFilter, orderBy("created", "asc")],
        };
    }, [user.customerId, user.email, onlyMine]);
    // The whole queue (no limit), sorted and paged here: a full sort without extra indexes
    const all = useLiveQueryAll<CtcPurchaseOrder>(ref, constraints, key);
    // The whole queue is loaded, so the status filter is applied here (no index)
    const sorted = useMemo(() => sortRows(status ? all.rows.filter((po) => po.tags?.poStatus?.code === status) : all.rows, view.sort), [all.rows, view.sort, status]);
    const page = useClientPage(sorted, all.loading, all.error, `${key}:${view.sort.field}:${view.sort.dir}:${status ?? ""}`);

    return (
        <CtcPanel>
            <Group mb="md" justify="space-between" align="center">
                <Text size="sm" c="dimmed">Open orders · {all.rows.length}</Text>
                <Group gap="sm" align="center">
                    <StatusSelect value={status} onChange={setStatus} />
                    {user.has("dealer") && !user.customerId && (
                        <Switch label="Only orders assigned to me" checked={onlyMine} onChange={(e) => setOnlyMine(e.currentTarget.checked)} />
                    )}
                    {backendSwitch}
                    <ListViewButton view={view} note="Sorts all open orders." />
                    <CtcSignatureButton />
                </Group>
            </Group>
            <CtcQueryError error={page.error} />
            <OrdersTable orders={page.rows} loading={page.loading} columns={view.columns} onColumnsChange={(columns) => view.apply({ columns, sort: view.sort })} emptyText="No open orders." />
            <CtcPager page={page} />
        </CtcPanel>
    );
};

/** Archive (all orders): Typesense (default) or Firestore, switched in the toolbar. */
export const CtcOrders = () => {
    const { backend, setBackend, control } = useCtcListBackend();
    return backend === "typesense" ?
        <CtcOrdersTypesense backendSwitch={control} onUseFirestore={() => setBackend("firestore")} /> :
        <CtcOrdersFirestore backendSwitch={control} />;
};

/** Watch list: Typesense (default) or Firestore, switched in the toolbar. */
export const CtcWatchList = () => {
    const { backend, setBackend, control } = useCtcListBackend();
    return backend === "typesense" ?
        <CtcWatchListTypesense backendSwitch={control} onUseFirestore={() => setBackend("firestore")} /> :
        <CtcWatchListFirestore backendSwitch={control} />;
};

export default CtcOrders;
